import cookies from 'js-cookie';
import Maybe from 'true-myth/maybe';

export const BRANDS = ['hotpads', 'zillow'] as const;
export type Brand = (typeof BRANDS)[number];

export const isBrand = (value: unknown): value is Brand => typeof value === 'string' && BRANDS.includes(value as Brand);

export const isHotpads = (value: unknown): value is 'hotpads' => value === 'hotpads';
export const isZillow = (value: unknown): value is 'zillow' => value === 'zillow';

export type HostnamePattern = {
  brand: Brand;
  patterns: Array<string>;
};

export type GetBrandOptions = {
  hostnamePatterns?: Array<HostnamePattern>;
  fallback?: Brand;
  devCookieName?: string | false;
};

export function getBrand(options?: GetBrandOptions): Maybe<Brand> {
  if (typeof window === 'undefined') {
    return Maybe.of(options?.fallback);
  }

  const cookieOverride = typeof document !== 'undefined' ? cookies.get('MULTIBRAND_SUPPORT:brand') : undefined;
  if (isBrand(cookieOverride)) {
    return Maybe.of(cookieOverride);
  }

  const multibrandSupport = 'MULTIBRAND_SUPPORT' in window ? window['MULTIBRAND_SUPPORT'] : undefined;
  if (multibrandSupport != null && typeof multibrandSupport === 'object') {
    const brand = 'brand' in multibrandSupport ? multibrandSupport.brand : undefined;
    if (isBrand(brand)) {
      return Maybe.of(brand);
    }
  }

  if (options?.hostnamePatterns) {
    const hostname = window.location.host;
    for (const { brand, patterns } of options.hostnamePatterns) {
      if (patterns.some((p) => hostname.includes(p))) {
        return Maybe.of(brand);
      }
    }
  }

  const devCookieName = options?.devCookieName ?? 'dev_brand';
  if (devCookieName !== false && isDevelopmentEnvironment()) {
    const devBrand = cookies.get(devCookieName);
    if (isBrand(devBrand)) {
      return Maybe.of(devBrand);
    }
    return Maybe.of(options?.fallback);
  }

  return Maybe.of(options?.fallback);
}

function isDevelopmentEnvironment(): boolean {
  return window.location.host.includes('localhost') || process.env.NODE_ENV === 'development';
}
